import React, { useState, useContext, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { useSelector } from 'react-redux'
import { Detail as DetailComponent, Confirm, useDrawer, SplitButton, InfoHeader, useFormatter } from 'src/mtska-frontend-app-component'
import { useDataProvider } from 'src/mtska-frontend-data-provider'
import { useTranslation } from 'react-i18next'
import { AppContext } from 'src/mtska-frontend-app-scaffolding'

const dataProviderName = 'campaigns/campaign'

/** La getOne espone `selectedSegments` (relazione); senza questo, `values` resta con selected_segments_ids: [] dopo il merge. */
const deriveSelectedSegmentIdsFromCampaignData = (data) => {
    if (!data) return []
    const raw = data.selected_segments_ids
    if (Array.isArray(raw) && raw.length > 0) {
        return raw.map((s) => (typeof s === 'string' ? s : (s?.id ?? s?._id))).filter(Boolean)
    }
    const rel = data.selectedSegments
    if (Array.isArray(rel) && rel.length > 0) {
        return rel.map((s) => s?.id ?? s?._id).filter(Boolean)
    }
    return []
}

const Create = (params) => {
    const { t } = useTranslation()
    const { formatDate } = useFormatter();
    const { close: closeDrawer } = useDrawer()
    const { userCapabilities, existingModules } = useContext(AppContext)
    const { item, save, loadOne, destroy, errorMessage, errors, saving, reloadCounter, doAction, uid, getOne } = useDataProvider(dataProviderName, params.multipart)
    const { modelName, createConfig: detailConfig } = existingModules[dataProviderName]

    const campaignRecordId = item?.data?._id ?? item?.data?.id
    const detailConfigOverride = useSelector((state) =>
        campaignRecordId ? state?.[dataProviderName]?.global?.detailConfigOverrideById?.[campaignRecordId] : undefined
    )
    /** Stesso criterio di Detail.jsx: le mutazioni sui field devono avvenire sul config effettivamente renderizzato. */
    const configForMutation = detailConfigOverride ?? detailConfig

    const [localError, setLocalError] = useState()
    const [localConfirm, setLocalConfirm] = useState()
    const [confirmDelete, setConfirmDelete] = useState()
    const [editing, setEditing] = useState(false)

    const [isStarted, setIsStarted] = useState(false);

    useEffect(() => {
        if (isStarted) return;

        loadOne(params.id ?? null);
    }, [isStarted, params.id])

    useEffect(() => {
        if (!item) return;
        if (isStarted) return;
        setIsStarted(true);
    }, [item])

    const detailReady = detailConfig && (!params?.id || item?.data)

    const handleSave = (data) => {
        // save({ ...data, filters: params.filters, canReloadCounter: false })
        //     .then((data) => params.handleClose(params.uid, data.item.data))
        //     .catch(() => console.error('catch handleSave'))
    }

    const handleDelete = (id) => {
        setConfirmDelete(
            <Confirm
                oklabel={t('Delete')}
                okcallable={() => { destroy && destroy(id); closeDrawer() }}
                kolabel={t('Cancel')}
                kocallable={() => setConfirmDelete(null)}
                title={t('Item elimination')}
            >
                {t('Are you sure to remove this element?')}
            </Confirm>
        )
    }

    const onProcessStepAction = (currentStep, stepTotal, actionInvoked, getValues, setValue) => {
        setLocalError()
        const values = getValues()

        const step1Fields = configForMutation.steps[1].columns[0].tabs[0].fieldsets[0].fields
        const step2Fields = configForMutation.steps[2].columns[0].tabs[0].fieldsets[0].fields
        const step3Fields = configForMutation.steps[3].columns[0].tabs[0].fieldsets[0].fields
        const step4Fields = configForMutation.steps[4].columns[0].tabs[0].fieldsets[0].fields

        step1Fields.selected_segments_ids.relation.selectionModel = values.selected_segments_ids;
        step2Fields.excluded_audience_leads_ids.relation.selectionModel = values.excluded_audience_leads_ids;
        step3Fields.campaign_leads_ids.relation.getValues = getValues;
        step3Fields.campaign_leads_ids.relation.setValue = setValue;
        step4Fields.excluded_outcomes_ids.relation.selectionModel = values.excluded_outcomes_ids;

        filterOutcomesTable(values, step4Fields);

        // da info a seleziona segmenti
        if (currentStep === 0 && actionInvoked === 1) {
            const stepValid = onValidateStepAction(currentStep, getValues)
            if (!stepValid) return false;
            // console.info('----- INVIO CAMPAGNA ', values);

            return new Promise((resolve, reject) => {
                doAction({
                    action: 'create',
                    model: 'campaigns/campaign',
                    data: values
                }).then(() => {

                    const campaignLeads = values.campaignLeads ?? [];
                    step3Fields.campaign_leads_ids.relation.campaignId = item.data.id;

                    if (campaignLeads.length == 0) {
                        const segIds = deriveSelectedSegmentIdsFromCampaignData(values)
                        if (segIds.length > 0) {
                            const campaignId = values.id ?? values._id ?? item?.data?.id ?? item?.data?._id
                            const segments = segIds
                                .map((s) => (typeof s === 'string' ? { id: s } : { id: s?.id ?? s?._id }))
                                .filter((x) => x.id)
                            const otherFilters = {}
                            if (campaignId) otherFilters.campaign = campaignId
                            if (segments.length) otherFilters.segments = segments
                            step2Fields.excluded_audience_leads_ids.relation.otherFilters = otherFilters
                            resolve({ goToStep: 2 })
                        } else {
                            resolve({})
                        }
                    } else if (campaignLeads.some(obj => !('operator_id' in obj))) {
                        // se qualche campaignLead non ha operatore assegnato
                        resolve({ goToStep: 3 });
                    } else {
                        // se tutte sono assegnate salto allo step 4
                        resolve({ goToStep: 4 });
                    }
                })
            });

            // return true;
        }
        // da seleziona segmenti a escludi lead
        else if (currentStep === 1 && actionInvoked === 1) {
            if (values.selected_segments_ids === null || values.selected_segments_ids === undefined || (Array.isArray(values.selected_segments_ids) && values.selected_segments_ids.length === 0)) {
                setLocalError(t('Select at least one segment'))
                return false
            } else {
                const campaignId = values.id ?? values._id ?? item?.data?.id ?? item?.data?._id
                const segmentIdsRaw = Array.isArray(values.selected_segments_ids) ? values.selected_segments_ids : []
                const segments = segmentIdsRaw
                    .map((s) => (typeof s === 'string' ? { id: s } : { id: s?.id ?? s?._id }))
                    .filter((x) => x.id)
                const otherFilters = {}
                if (campaignId) otherFilters.campaign = campaignId
                if (segments.length) otherFilters.segments = segments
                step2Fields.excluded_audience_leads_ids.relation.otherFilters = otherFilters

                return new Promise((resolve, reject) => {
                    doAction({
                        action: 'create',
                        model: 'campaigns/campaign',
                        data: values
                    }).then(() => {
                        resolve({});
                    })
                });
            }
        }
        //da esclusione lead a assegna operatore
        else if (currentStep === 2 && actionInvoked === 1) {
            step3Fields.campaign_leads_ids.relation.campaignId = values.id;

            return new Promise((resolve, reject) => {
                doAction({
                    action: 'createWithExcludedLeads',
                    model: 'campaigns/campaign',
                    data: {
                        id: values.id,
                        excluded_audience_leads_ids: Array.isArray(values.excluded_audience_leads_ids)
                            ? values.excluded_audience_leads_ids.map(l => l.id ?? l._id ?? l)
                            : []
                    }
                }).then(() => {
                    resolve({});
                })
            });

        }
        //da seleziona operatore a esiti campagne
        else if (currentStep === 3 && actionInvoked === 1) {
            const campaignLeads = values.campaignLeads ?? []
            const hasMissingOperatorId = campaignLeads.some(obj => !obj.hasOwnProperty('operator_id'));

            if (hasMissingOperatorId) {
                const campaignId = values.id ?? values._id ?? item?.data?.id ?? item?.data?._id
                return new Promise((resolve, reject) => {
                    setLocalConfirm(
                        <Confirm
                            oklabel={t('OK')}
                            okcallable={() => {
                                const finishOk = (leads) => {
                                    const safeLeads = Array.isArray(leads) ? leads : []
                                    const ids = safeLeads.map((l) => l?.id ?? l?._id).filter(Boolean)
                                    const setOpts = { shouldValidate: true, shouldDirty: true }
                                    setValue('campaignLeads', safeLeads, setOpts)
                                    setValue('campaign_leads_ids', ids, setOpts)
                                    flushSync(() => setLocalConfirm(null))
                                    resolve({})
                                }

                                return doAction({
                                    action: 'deleteNotAssignedLeads',
                                    model: 'campaigns/campaign',
                                    data: { id: campaignId }
                                })
                                    .then(() => loadOne(campaignId).catch(() => null))
                                    .then(() => {
                                        let freshRecord = getOne(campaignId)
                                        if (!freshRecord) {
                                            const altId = item?.data?._id ?? item?.data?.id
                                            if (altId != null && String(altId) !== String(campaignId)) {
                                                freshRecord = getOne(altId)
                                            }
                                        }
                                        let leads = freshRecord?.campaignLeads
                                        if (!Array.isArray(leads)) {
                                            leads = (getValues().campaignLeads ?? []).filter((cl) =>
                                                Object.prototype.hasOwnProperty.call(cl, 'operator_id')
                                            )
                                        }
                                        finishOk(leads)
                                    })
                                    .catch((err) => {
                                        flushSync(() => setLocalConfirm(null))
                                        reject(err)
                                        throw err
                                    })
                            }}
                            kolabel={t('Cancel')}
                            kocallable={() => {
                                setLocalConfirm(null)
                                resolve(false)
                            }}
                            title={t('Drop unassigned leads?')}
                        >
                            {t('If you proceed, the unassigned leads will be discarded from the campaign')}
                        </Confirm>
                    )
                })
            }
        }
        //da esiti campagne a recap
        else if (currentStep === 4 && actionInvoked === 1) {


        }
        //da recap a conferma
        else if (currentStep === 5 && actionInvoked === 1) {
            return new Promise((resolve, reject) => {
                doAction({
                    action: 'create',
                    model: 'campaigns/campaign',
                    data: { ...values, editingstatus: values.editingstatus == 'campaign_new' ? 'campaign_draft' : values.editingstatus }
                }).then(() => {
                    resolve({});
                })
            });
        }
        //da conferma a chiusura
        else if (currentStep === 6 && actionInvoked === 1) {
            closeDrawer();
        }
        return true
    }

    const filterOutcomesTable = (values, step4Fields) => {
        if (!values.mode) return;
        const filterModel = {
            items: [
                {
                    field: 'mode',
                    operator: 'equals',
                    value: values.mode
                }
            ]
        }

        step4Fields.excluded_outcomes_ids.filters = filterModel;
    }

    const onValidateStepAction = (currentStep, getValues, setValue, setError, resultValue) => {
        if (resultValue === false) return false
        const values = getValues()
        const step = configForMutation.steps[currentStep]
        if (!step) return true
        let stepValid = true
        const errors = []
        step.columns.forEach(col => col.tabs.forEach(tab => tab.fieldsets.forEach(fs => {
            Object.entries(fs.fields).forEach(([key, field]) => {
                if (field.required && !values[key]) {
                    stepValid = false;
                    errors.push(t(key))
                }
            })
        })))

        if (!stepValid) setLocalError(t('Complete all required fields before proceeding: ' + errors.join(',')))
        return stepValid
    }

    const startAt = new Date();
    return (
        <>
            {detailReady && (
                <DetailComponent
                    key={params?.id ?? 'new-campaign'}
                    values={{
                        name: null,
                        type: null,
                        channel: null,
                        editingstatus: 'campaign_new',
                        start_at: startAt,
                        end_at: null,
                        salesDisk: null,
                        selected_segments_ids: [],
                        excluded_audience_leads_ids: [],
                        excluded_outcomes_ids: [],
                        ...item?.data,
                        selected_segments_ids: deriveSelectedSegmentIdsFromCampaignData(item?.data),
                    }}
                    modelName={modelName}
                    detailConfig={detailConfig}
                    dataProviderName={dataProviderName}
                    onEditing={setEditing}
                    save={handleSave}
                    saving={saving}
                    errors={errors}
                    errorMessage={errorMessage}
                    alertMessage={localError}
                    canSave={userCapabilities && userCapabilities[dataProviderName + '.edit']}
                    title={item?.data?.name ?? t('New campaign')}
                    subtitle={(item?.data?.start_at ? formatDate(item?.data?.start_at) : '') + " " + (item?.data?.end_at ? formatDate(item?.data?.end_at) : '').trim()}
                    header={<InfoHeader view="campaign" item={item?.data} />}
                    rightButtons={[
                        <SplitButton
                            actions={[{
                                disabled: userCapabilities && !userCapabilities[dataProviderName + '.delete'],
                                icon: 'faTrash',
                                label: t('Delete'),
                                onClick: () => handleDelete(item.data.id)
                            }]}
                        />
                    ]}
                    onProcessStepAction={onProcessStepAction}
                    onValidateStepAction={onValidateStepAction}
                    {...params}
                />
            )}
            {confirmDelete}
            {localConfirm}
        </>
    )
}

export default Create